import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const summarySteps = [
  { label: '课堂材料', icon: 'ri-upload-cloud-2-line', color: 'primary' },
  { label: '课堂结构', icon: 'ri-layout-5-line', color: 'accent' },
  { label: '评价证据', icon: 'ri-file-search-line', color: 'primary' },
  { label: '维度判断', icon: 'ri-scales-3-line', color: 'accent' },
  { label: '质量分层', icon: 'ri-stack-line', color: 'primary' },
  { label: '改进建议', icon: 'ri-lightbulb-line', color: 'accent' },
];

const fiveCapabilities = [
  '结构可理解',
  '证据可回看',
  '维度可诊断',
  '分档可验证',
  '建议可执行',
];

export default function P45WrapUp({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.06);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.05);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div ref={titleRef} className="text-center mb-8 md:mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-gold border-accent-200/30 mb-5">
            <span className="gold-dot" />
            <span className="text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em]">第三部分小结</span>
          </div>
          <h2 className="font-heading text-2xl md:text-4xl lg:text-[2.75rem] font-bold text-foreground-950 transition-all duration-1000 delay-100">
            把课堂转化为<br />
            <span className="text-primary-600">结构、证据、评价与改进</span>
          </h2>
        </div>

        {/* 大字链路 */}
        <div className="max-w-5xl mx-auto mb-8 transition-all duration-1000 delay-200">
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-3">
            {summarySteps.map((step, i) => (
              <div key={step.label} className="flex items-center gap-0">
                <div className={`flex items-center gap-2 px-4 py-3 rounded-2xl ${step.color === 'primary' ? 'glass-liquid border-primary-200/30' : 'glass-liquid-gold border-accent-200/30'} transition-all duration-300 hover:scale-105`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${step.color === 'primary' ? 'glass-blue border-primary-200/40' : 'glass-gold border-accent-200/40'}`}>
                    <i className={`${step.icon} text-sm ${step.color === 'primary' ? 'text-primary-600' : 'text-accent-600'}`} />
                  </div>
                  <span className="text-sm font-body font-bold text-foreground-800 whitespace-nowrap">{step.label}</span>
                </div>
                {i < summarySteps.length - 1 && (
                  <i className="ri-arrow-right-s-line text-foreground-300 text-lg mx-1.5" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 五项能力 */}
        <div className="max-w-3xl mx-auto mb-6 transition-all duration-1000 delay-400">
          <div className="p-5 rounded-2xl glass-strong border-primary-200/30">
            <h3 className="font-heading text-sm font-bold text-foreground-800 mb-4 text-center">评价结果必须具备五项能力</h3>
            <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
              {fiveCapabilities.map((cap) => (
                <span key={cap} className="px-3 py-1.5 rounded-full glass border-white/50 text-xs font-body font-semibold text-foreground-700">
                  {cap}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* 底部总结 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 max-w-5xl mx-auto mb-6 transition-all duration-1000 delay-600">
          <div className="p-5 rounded-2xl glass-liquid border-primary-200/30 text-center">
            <div className="relative z-10">
              <i className="ri-checkbox-circle-line text-xl text-primary-500 mb-2" />
              <p className="text-sm font-body font-semibold text-foreground-800 mb-1">亮点与短板并存</p>
              <p className="text-xs text-foreground-500 font-body">本科03案例转化突出但认知铺垫不足；互动能力需在真实课堂中验证</p>
            </div>
          </div>
          <div className="p-5 rounded-2xl glass-liquid-gold border-accent-200/30 text-center">
            <div className="relative z-10">
              <i className="ri-group-line text-xl text-accent-500 mb-2" />
              <p className="text-sm font-body font-semibold text-foreground-800 mb-1">专家、学生、AI三方</p>
              <p className="text-xs text-foreground-500 font-body">不同视角需要被解释，而不是被一个总分抹平</p>
            </div>
          </div>
          <div className="p-5 rounded-2xl glass-liquid border-primary-200/30 text-center">
            <div className="relative z-10">
              <i className="ri-loop-left-line text-xl text-primary-500 mb-2" />
              <p className="text-sm font-body font-semibold text-foreground-800 mb-1">超出单节课</p>
              <p className="text-xs text-foreground-500 font-body">服务教师复盘、专家网评、管理决策，汇总为成长档案和区域趋势</p>
            </div>
          </div>
        </div>

        {/* 连接下一页 */}
        <div className="max-w-2xl mx-auto text-center transition-all duration-1000 delay-900">
          <div className="inline-flex items-center gap-2 px-5 py-3 rounded-xl glass-gold border-accent-200/30">
            <i className="ri-arrow-right-line text-accent-500" />
            <span className="text-sm font-body text-foreground-600">
              进入第四部分：<strong className="text-primary-700">应用服务与质量治理</strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}